import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { socketService } from "../../../services/socket";
import { useProjects } from "./ProjectContext";

interface TagCategory {
  id: string;
  name?: string;
  color: string;
  order: number;
}

interface Tag {
  id: string;
  label: string;
  categoryId: string;
  order: number;
}

export interface Camera {
  id: string;
  name: string;
  operator: string;
  order: number;
}

export interface Director {
  id: string;
  name: string;
  order: number;
}

export interface VtrTag {
  id: string;
  label: string;
  color: string;
  order: number;
}

interface TagsContextType {
  categories: TagCategory[];
  tags: Tag[];
  cameras: Camera[];
  directors: Director[];
  vtrTags: VtrTag[];
  applyData: (data: any) => void;
  getTagColor: (tagId: string) => string;
  getTagLabel: (tagId: string) => string;
  getCameraName: (cameraId: string) => string;
  getVtrTagLabel: (tagId: string) => string;
  getVtrTagColor: (tagId: string) => string;
}

export const TagsContext = createContext<TagsContextType | undefined>(undefined);

export const TagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { activeProject } = useProjects();
  const [categories, setCategories] = useState<TagCategory[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [directors, setDirectors] = useState<Director[]>([]);
  const [vtrTags, setVtrTags] = useState<VtrTag[]>([]);

  const applyData = useCallback(
    (data: {
      categories?: TagCategory[];
      tags?: Tag[];
      cameras?: Camera[];
      directors?: Director[];
      vtrTags?: VtrTag[];
    }) => {
      setCategories(data.categories || []);
      setTags(data.tags || []);
      setCameras(data.cameras || []);
      setDirectors(data.directors || []);
      setVtrTags(data.vtrTags || []);
    },
    [],
  );

  const fetchData = useCallback(() => {
    if (!socketService.connected || !activeProject?.id) return;
    socketService.emit("get-tags-data", activeProject.id, applyData);
  }, [applyData, activeProject?.id]);

  // Fetch whenever the active project changes (server scopes tags by projectId)
  useEffect(() => {
    if (!activeProject?.id) return;
    fetchData();
  }, [activeProject?.id, fetchData]);

  useEffect(() => {
    const registerAsEditor = () => {
      socketService.emit("register-editor");
    };

    const onConnect = () => {
      registerAsEditor();
      fetchData();
    };

    if (socketService.connected) onConnect();
    socketService.on("connect", onConnect);
    socketService.on("tags-data-updated", applyData);

    return () => {
      socketService.off("connect", onConnect);
      socketService.off("tags-data-updated", applyData);
    };
  }, [applyData, fetchData]);

  const value = useMemo<TagsContextType>(
    () => ({
      categories,
      tags,
      cameras,
      directors,
      vtrTags,
      applyData,
      getTagColor: (tagId) => {
        const tag = tags.find((t) => t.id === tagId);
        if (!tag) return "#888";
        const category = categories.find((c) => c.id === tag.categoryId);
        return category ? category.color : "#888";
      },
      getTagLabel: (tagId) => {
        const tag = tags.find((t) => t.id === tagId);
        return tag ? tag.label || (tag as any).name || tagId : tagId;
      },
      getCameraName: (cameraId) => {
        // Anclamos el match a "cam" como prefijo real de id de cámara
        // (seguido de espacio/_/-, o de letra+digitos como en "camA01").
        // Sin esa ancla, "Camila" se reescribía como "CAM ila" y se renderizaba
        // como un chip naranja gigante con el resto de la frase.
        const stripCamPrefix = (s: string) =>
          s.replace(/^&?(amp;)?/i, "").replace(/^cam(?=[\s_-]|[a-z]?\d)/i, "CAM");
        // Defensa: cualquier "nombre" que parezca frase libre (muchas
        // palabras o demasiado largo) no es una cámara real — lo más
        // probable es texto suelto que entró por un Quill mention legacy
        // o por una entrada mal creada en la tabla `cameras`. Devolvemos
        // "" para que el render (que usa `camName ? <Badge> : null`)
        // simplemente lo omita en vez de mostrar un chip gigante.
        const looksLikeCameraName = (s: string) =>
          s.length > 0 && s.length <= 30 && s.trim().split(/\s+/).length <= 5;
        const cam = cameras.find((c) => c.id === cameraId);
        if (cam?.name) {
          const name = stripCamPrefix(cam.name);
          return looksLikeCameraName(name) ? name : "";
        }
        if (!looksLikeCameraName(cameraId)) return "";
        return stripCamPrefix(cameraId);
      },
      getVtrTagLabel: (tagId) => {
        const vt = vtrTags.find((t) => t.id === tagId);
        return vt ? vt.label : tagId;
      },
      getVtrTagColor: (tagId) => {
        const vt = vtrTags.find((t) => t.id === tagId);
        return vt ? vt.color : "#888";
      },
    }),
    [categories, tags, cameras, directors, vtrTags, applyData],
  );

  return <TagsContext.Provider value={value}>{children}</TagsContext.Provider>;
};

export const useTags = () => {
  const context = useContext(TagsContext);
  if (!context) {
    throw new Error("useTags must be used within a TagsProvider");
  }
  return context;
};
